import { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';

const TOKEN_KEY = 'admin_token';
const USERNAME_KEY = 'admin_username';
const ROLE_KEY = 'auth_role';

export type AuthRole = 'admin' | 'user';

/** Listener account role from DB (profile); JWT may still say `user` until re-login */
export type AppRole = 'user' | 'admin' | 'superuser';

type AuthContextType = {
  isAdmin: boolean;
  /** Admin JWT or DB admin/superuser — can moderate all comments */
  canModerate: boolean;
  /** Numeric user id when profile has a `users` row */
  userId: number | null;
  appRole: AppRole | null;
  planSlug: string;
  isPro: boolean;
  isLoggedIn: boolean;
  role: AuthRole | null;
  username: string | null;
  avatarUrl: string | null;
  setAvatarUrl: (url: string | null) => void;
  refreshProfile: () => Promise<void>;
  login: (username: string, password: string) => Promise<AuthRole>;
  register: (username: string, password: string) => Promise<void>;
  logout: () => void;
  token: string | null;
};

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken] = useState<string | null>(() => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) return null;
    const r = localStorage.getItem(ROLE_KEY);
    if (r === 'admin' || r === 'user') return t;
    try {
      const payload = JSON.parse(atob(t));
      if (payload?.role === 'admin' || payload?.role === 'user') return t;
    } catch {}
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USERNAME_KEY);
    localStorage.removeItem(ROLE_KEY);
    return null;
  });
  const [username, setUsername] = useState<string | null>(() => localStorage.getItem(USERNAME_KEY));
  const [role, setRole] = useState<AuthRole | null>(() => {
    const r = localStorage.getItem(ROLE_KEY);
    if (r === 'admin' || r === 'user') return r;
    const t = localStorage.getItem(TOKEN_KEY);
    if (t) {
      try {
        const payload = JSON.parse(atob(t));
        if (payload?.role === 'admin' || payload?.role === 'user') return payload.role;
      } catch {}
    }
    return null;
  });
  const [avatarUrl, setAvatarUrlState] = useState<string | null>(null);
  const [userId, setUserId] = useState<number | null>(null);
  const [appRole, setAppRole] = useState<AppRole | null>(null);
  const [planSlug, setPlanSlug] = useState<string>('free');

  const refreshProfile = useCallback(async () => {
    if (!token) return;
    try {
      const { getProfile } = await import('../api/client');
      const profile = await getProfile();
      if (profile.username) {
        setUsername(profile.username);
        localStorage.setItem(USERNAME_KEY, profile.username);
      }
      setAvatarUrlState(profile.avatar_url ?? null);
      const uid = profile.user_id;
      setUserId(typeof uid === 'number' ? uid : null);
      const ar = profile.app_role;
      if (ar === 'user' || ar === 'admin' || ar === 'superuser') setAppRole(ar);
      else setAppRole(null);
      setPlanSlug(profile.plan_slug ?? 'free');
    } catch {
      // Keep the last-known auth/profile state on transient failures so
      // moderator/admin UI permissions do not disappear mid-session.
    }
  }, [token]);

  useEffect(() => {
    if (token) refreshProfile();
    else {
      setAvatarUrlState(null);
      setUserId(null);
      setAppRole(null);
      setPlanSlug('free');
    }
  }, [token, refreshProfile]);

  const setAvatarUrl = useCallback(async (url: string | null) => {
    const { updateProfileAvatar } = await import('../api/client');
    const profile = await updateProfileAvatar(url);
    setAvatarUrlState(profile.avatar_url ?? null);
  }, []);

  useEffect(() => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) return;
    try {
      const payload = JSON.parse(atob(t));
      const tokenRole = payload?.role === 'admin' || payload?.role === 'user' ? payload.role : null;
      if (tokenRole) {
        setRole(tokenRole);
        localStorage.setItem(ROLE_KEY, tokenRole);
      }
    } catch {}
  }, []);

  const setAuth = useCallback((t: string | null, u: string | null, r: AuthRole | null) => {
    setToken(t);
    setUsername(u);
    setRole(r);
    if (t) {
      localStorage.setItem(TOKEN_KEY, t);
      localStorage.setItem(USERNAME_KEY, u ?? '');
      localStorage.setItem(ROLE_KEY, r ?? '');
    } else {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USERNAME_KEY);
      localStorage.removeItem(ROLE_KEY);
    }
  }, []);

  const login = useCallback(async (user: string, password: string): Promise<AuthRole> => {
    const { login: apiLogin } = await import('../api/client');
    const { token: t, username: u, role: r } = await apiLogin(user, password);
    setAuth(t, u, r);
    return r;
  }, [setAuth]);

  const register = useCallback(async (user: string, password: string) => {
    const { register: apiRegister } = await import('../api/client');
    const { token: t, username: u, role: r } = await apiRegister(user, password);
    setAuth(t, u, r);
  }, [setAuth]);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USERNAME_KEY);
    localStorage.removeItem(ROLE_KEY);
    setToken(null);
    setUsername(null);
    setRole(null);
    setAvatarUrlState(null);
    setUserId(null);
    setAppRole(null);
    setPlanSlug('free');
  }, []);

  const canModerate = useMemo(
    () => role === 'admin' || appRole === 'superuser' || appRole === 'admin',
    [role, appRole]
  );
  const isPro = useMemo(() => planSlug === 'pro', [planSlug]);

  const value: AuthContextType = {
    isAdmin: role === 'admin',
    canModerate,
    userId,
    appRole,
    planSlug,
    isPro,
    isLoggedIn: !!token,
    role,
    username,
    avatarUrl,
    setAvatarUrl,
    refreshProfile,
    login,
    register,
    logout,
    token,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
